import "./UiMessageBlockComponent.js";
import "./NextBestActionBlockComponent.js";
import "./AcceptedNextBestActionBlockComponent.js";
import { type RenderableContentBlock, type AcceptableNextBestAction } from "../types.js";
import { sharedSheet } from "../styles/sharedStyles.js";
import componentStylesText from "./ParticipantMessageSubListComponent.css?inline";

const componentSheet = new CSSStyleSheet();
componentSheet.replaceSync(componentStylesText);

/**
 * ParticipantMessageSubListComponent — A container component for a list of message blocks from the same participant.
 *
 * It renders the following structure:
 * <div class="chat-message chat-message--[role]">
 *   <span class="chat-message__fulltext">[Aggregated Text]</span>
 *   <ul class="chat-message__blocks">
 *     <li>[Child Component 1]</li>
 *     <li>[Child Component 2]</li>
 *   </ul>
 * </div>
 *
 * Supported content block types (case-insensitive):
 * - SIMPLE_TEXT / string: Simple text message (jt-simple-text-block)
 * - UI_MESSAGE: UI message for assistant (jt-ui-message-block)
 * - JOKE: Joke content block (jt-joke-block)
 * - PROJECT: Project content block (jt-project-block)
 * - TESTIMONIAL: Testimonial content block (jt-testimonial-block)
 * - ERROR: Error content block (jt-error-block)
 * - NOT_FOUND: Not found content block (jt-not-found-block)
 *
 * Attributes:
 * - role: "assistant" or "user"
 */
export class ParticipantMessageSubListComponent extends HTMLElement {
    private container: HTMLDivElement | null = null;
    private fulltextSpan: HTMLSpanElement | null = null;
    private list: HTMLUListElement | null = null;
    private loadingBlock: HTMLElement | null = null;
    private boundUpdateFullText = this.updateFullText.bind(this);

    /**
     * Toggles the loading state of the sub-list.
     */
    set isLoading(value: boolean) {
        if (!this.list) {
            return;
        }

        if (value) {
            if (!this.loadingBlock) {
                this.loadingBlock = document.createElement("jt-loading-block");
                const role = this.getAttribute("role") || "assistant";
                this.loadingBlock.setAttribute("role", role);
                const li = document.createElement("li");
                li.className = "jt-loading-wrapper"; // Optional: for easier identification
                li.appendChild(this.loadingBlock);
                this.list.appendChild(li);
                this.updateFullText();
            }
        } else {
            if (this.loadingBlock) {
                const li = this.loadingBlock.parentElement;
                if (li && li.tagName.toLowerCase() === "li") {
                    li.remove();
                } else {
                    this.loadingBlock.remove();
                }
                this.loadingBlock = null;
                this.updateFullText();
            }
        }
    }

    get isLoading(): boolean {
        return !!this.loadingBlock;
    }

    /**
     * Returns true if there are no message blocks in the list.
     */
    get isEmpty(): boolean {
        return (this.list?.children.length || 0) === 0;
    }

    /**
     * Specifies which attributes should be monitored for changes.
     */
    static get observedAttributes(): string[] {
        return ["role"];
    }

    /**
     * Called when the element is first connected to the document's DOM.
     */
    connectedCallback(): void {
        if (!this.shadowRoot) {
            this.render();
        }
        this.updateRole();
        this.updateFullText();
        this.shadowRoot!.addEventListener("text-update", this.boundUpdateFullText);
    }

    /**
     * Called when the element is removed from the document's DOM.
     */
    disconnectedCallback(): void {
        this.shadowRoot?.removeEventListener("text-update", this.boundUpdateFullText);
    }

    /**
     * Called when one of the observed attributes changes.
     */
    attributeChangedCallback(name: string, _oldValue: string | null, _newValue: string | null): void {
        if (name === "role") {
            this.updateRole();
        }
    }

    /**
     * Creates the internal DOM structure.
     */
    private render(): void {
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [sharedSheet, componentSheet];

        this.container = document.createElement("div");
        this.container.className = "chat-message";

        this.fulltextSpan = document.createElement("span");
        this.fulltextSpan.className = "chat-message__fulltext";

        this.list = document.createElement("ul");
        this.list.className = "chat-message__blocks";

        this.container.appendChild(this.fulltextSpan);
        this.container.appendChild(this.list);
        
        // Append container to shadow root
        shadow.appendChild(this.container);
    }

    /**
     * Updates the role-specific classes and labels.
     */
    private updateRole(): void {
        if (!this.container) {
            return;
        }

        const role = this.getAttribute("role") || "assistant";
        this.container.classList.remove("chat-message--assistant", "chat-message--user");
        this.container.classList.add(`chat-message--${role}`);

        // Update role on all existing blocks
        if (this.list) {
            const blocks = this.list.querySelectorAll("li > *");
            blocks.forEach(block => block.setAttribute("role", role));
        }
        
        this.updateFullText();
    }

    /**
     * Adds new message blocks to the list.
     * @param data An array of RenderableContentBlock objects to render.
     */
    addBlocks(data: RenderableContentBlock[]): void {
        if (!this.list) {
            return;
        }

        const role = this.getAttribute("role") || "assistant";
        data.forEach(item => {
            let block: HTMLElement;
            const dataRecord = item as unknown as Record<string, unknown>;
            const type = dataRecord.type;
            // Normalize type to uppercase for case-insensitive comparison
            const normalizedType = typeof type === "string" ? type.toUpperCase() : "";
            
            switch (normalizedType) {
                case "SIMPLE_TEXT":
                    block = document.createElement("jt-simple-text-block");
                    block.setAttribute("text", typeof dataRecord.text === "string" ? dataRecord.text : "");
                    break;
                case "UI_MESSAGE":
                    block = document.createElement("jt-ui-message-block");
                    block.setAttribute("text", typeof dataRecord.text === "string" ? dataRecord.text : "");
                    break;
                case "JOKE":
                    block = document.createElement("jt-joke-block");
                    block.setAttribute("block", JSON.stringify(item));
                    break;
                case "PROJECT":
                    block = document.createElement("jt-project-block");
                    block.setAttribute("block", JSON.stringify(item));
                    break;
                case "TESTIMONIAL":
                    block = document.createElement("jt-testimonial-block");
                    block.setAttribute("block", JSON.stringify(item));
                    break;
                case "ERROR":
                    block = document.createElement("jt-error-block");
                    break;
                case "NOT_FOUND":
                    block = document.createElement("jt-not-found-block");
                    break;
                case "NEXT_BEST_ACTION":
                    block = document.createElement("jt-next-best-action-block");
                    block.setAttribute("nba", JSON.stringify((item as { nba: AcceptableNextBestAction }).nba));
                    break;
                case "ACCEPTED_NEXT_BEST_ACTION":
                    block = document.createElement("jt-accepted-next-best-action-block");
                    block.setAttribute("nba", JSON.stringify((item as { nba: AcceptableNextBestAction }).nba));
                    break;
                default:
                    // Fallback: render as simple text block with JSON stringified data
                    block = document.createElement("jt-simple-text-block");
                    block.setAttribute("text", JSON.stringify(item));
            }

            block.setAttribute("role", role);
            const li = document.createElement("li");
            li.appendChild(block);
            this.list!.appendChild(li);
        });

        this.updateFullText();
    }

    /**
     * Aggregates text from all child blocks for accessibility.
     */
    private updateFullText(): void {
        if (!this.fulltextSpan || !this.list) {
            return;
        }
        
        const texts: string[] = [];
        
        // Generic selector: find all children of <li> elements within our list
        const blocks = Array.from(this.list.querySelectorAll("li > *"));
        
        blocks.forEach((block) => {
            // Check if the block implements the accessibilityText property
            const accessibleBlock = block as HTMLElement & { accessibilityText?: string };
            const text = accessibleBlock.accessibilityText;
            if (text) {
                texts.push(text);
            }
        });

        const combinedText = texts.join(" ");
        const role = this.getAttribute("role") || "assistant";
        const speakerPrefix = role === "assistant" ? "Assistant: " : "You: ";
        this.fulltextSpan.textContent = speakerPrefix + combinedText;
    }
}

if (!customElements.get("jt-participant-messages-sub-list")) {
    customElements.define("jt-participant-messages-sub-list", ParticipantMessageSubListComponent);
}
